import React, { useState, useRef, useEffect } from "react";
import { Form, Button, Row, Col, ButtonGroup } from "react-bootstrap";
import { Stage, Layer, Text, Rect, Circle, Image as KonvaImage, Transformer, Group, Line, Ellipse } from "react-konva";
import PrimaryButton from "../../components/common/PrimaryButton";
import SecondaryButton from "../../components/common/SecondaryButton";
import TemplateService from "../../services/TemplateService.js";
import {
  CANVAS_STATE_COMMENT_PREFIX,
  encodeCanvasState,
  parseTemplateHtmlToCanvas,
} from "../../utils/templateCanvasUtils";
import {
  showDeleteConfirm,
  showErrorAlert,
  showSuccessAlert,
  showWarningAlert,
} from "../../utils/sweetAlertUtils";

const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

const TemplateCreation = ({ onBack, initialTemplate = null, editTemplate = null }) => {
  const isEditMode = !!editTemplate;
  const isFromSample = !!initialTemplate && !isEditMode;
  const loadTemplate = editTemplate || initialTemplate;
  const sampleImageUrlRef = useRef(null);
  const [templateName, setTemplateName] = useState("");
  const [templateCategory, setTemplateCategory] = useState("");
  const [bgImage, setBgImage] = useState(null);
  const [bgImageFile, setBgImageFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [imageDimensions, setImageDimensions] = useState({ width: 500, height: 500 });

  // Konva elements
  const [textElements, setTextElements] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [tool, setTool] = useState("text"); // 'text', 'rect', 'circle'

  // Text formatting
  const [textFormat, setTextFormat] = useState({
    fontSize: 24,
    fontFamily: "Arial",
    fontStyle: "normal", // normal, bold, italic, bold italic
    textDecoration: "", // "", "underline"
    fill: "#000000",
  });

  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const textInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [bgKonvaImage, setBgKonvaImage] = useState(null);
  const [imageError, setImageError] = useState("");

  // Load background image for Konva
  useEffect(() => {
    if (bgImage) {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setBgKonvaImage(img);
      };
      img.onerror = (error) => {
        console.error("Error loading background image:", error);
      };
      img.src = bgImage;
    } else {
      setBgKonvaImage(null);
    }
  }, [bgImage]);

  useEffect(() => {
    if (!transformerRef.current || !stageRef.current) return;

    const stage = stageRef.current;
    const transformer = transformerRef.current;

    if (selectedId) {
      const selectedNode = stage.findOne(`#${selectedId}`);
      if (selectedNode) {
        transformer.nodes([selectedNode]);
      }
    } else {
      transformer.nodes([]);
    }

    transformer.getLayer()?.batchDraw();
  }, [selectedId]);


  useEffect(() => {
    if (!loadTemplate) return;

    if (isEditMode) {
      setTemplateName(loadTemplate.name || "");
    }

    setTemplateCategory(loadTemplate.templateCategory || "");

    const applyParsedTemplate = (width, height) => {
      const parsed = parseTemplateHtmlToCanvas(loadTemplate.templateBody, {
        width,
        height,
      });

      if (parsed.imageSize) {
        setImageDimensions(parsed.imageSize);
      }

      setTextElements(parsed.textElements);
      setShapes(parsed.shapes);
    };

    if (loadTemplate.templateImage) {
      const imageUrl =
        loadTemplate.templateImage.startsWith("http://") ||
        loadTemplate.templateImage.startsWith("https://") ||
        loadTemplate.templateImage.startsWith("data:") ||
        loadTemplate.templateImage.startsWith("blob:")
          ? loadTemplate.templateImage
          : `${BASE_URL}${loadTemplate.templateImage}`;

      sampleImageUrlRef.current = imageUrl;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const width = img.naturalWidth || img.width || 500;
        const height = img.naturalHeight || img.height || 500;
        setImageDimensions({ width, height });
        setBgImage(imageUrl);
        applyParsedTemplate(width, height);
      };
      img.onerror = () => {
        setImageDimensions({ width: 500, height: 500 });
        applyParsedTemplate(500, 500);
      };
      img.src = imageUrl;
      return;
    }

    applyParsedTemplate(imageDimensions.width, imageDimensions.height);
  }, [loadTemplate, isEditMode]);


  const getCategoryId = (categoryName) => {
    const categories = {
      Marketing: 1,
      Education: 2,
      Health: 3,
      Property: 4,
    };
    return categories[categoryName] || 0;
  };

  const cropImageTo500x500 = (file, callback) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;

        if (width <= 500 && height <= 500) {
          setImageDimensions({ width, height });
          callback(file, e.target.result);
          return;
        }

        const targetSize = 500;
        let sourceX = 0;
        let sourceY = 0;
        let sourceWidth = width;
        let sourceHeight = height;

        const imageAspect = width / height;
        const targetAspect = 1;

        if (imageAspect > targetAspect) {
          sourceWidth = height * targetAspect;
          sourceX = (width - sourceWidth) / 2;
        } else {
          sourceHeight = width / targetAspect;
          sourceY = (height - sourceHeight) / 2;
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetSize;
        canvas.height = targetSize;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          targetSize,
          targetSize
        );

        canvas.toBlob((blob) => {
          const croppedFile = new File([blob], file.name, {
            type: file.type || 'image/png',
            lastModified: Date.now(),
          });

          const croppedUrl = URL.createObjectURL(blob);
          setImageDimensions({ width: targetSize, height: targetSize });
          callback(croppedFile, croppedUrl);
        }, file.type || 'image/png', 0.95);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageError("");

    if (!file.type.startsWith("image/")) {
      const message = "Please select a valid image file (JPEG, PNG, GIF, or WebP).";
      setImageError(message);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await showWarningAlert("Invalid File", message);
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      const message = "Image size must be 5 MB or less.";
      setImageError(message);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await showWarningAlert("File Too Large", message);
      return;
    }

    setFileName(file.name);
    cropImageTo500x500(file, (processedFile, imageUrl) => {
      setBgImageFile(processedFile);
      setBgImage(imageUrl);
    });
  };

  // Handle stage click
  const handleStageClick = (e) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedId(null);
      return;
    }

    const id = e.target.id();
    if (id) {
      setSelectedId(id);
    }
  };


  // Function to start editing a shape's text
  const startEditingShapeText = (shapeId) => {
    const shape = shapes.find((s) => s.id === shapeId);
    if (!shape) return;

    setEditingId(shapeId);
    setEditingText(shape.text || "");

    setTimeout(() => {
      if (!textInputRef.current || !stageRef.current) return;

      try {
        const stage = stageRef.current;
        const container = stage.container();
        if (!container) return;

        const groupNode = stage.findOne(`#${shapeId}`);
        if (!groupNode) return;

        const shapePosition = groupNode.absolutePosition();
        const stageBox = container.getBoundingClientRect();

        let textX, textY, textWidth, textHeight;

        if (shape.id.startsWith("rect-")) {
          textX = shapePosition.x + 5;
          textY = shapePosition.y + 5;
          textWidth = shape.width - 10;
          textHeight = shape.height - 10;
        } else if (shape.id.startsWith("circle-")) {
          textX = shapePosition.x - shape.radius + 5;
          textY = shapePosition.y - shape.radius + 5;
          textWidth = shape.radius * 2 - 10;
          textHeight = shape.radius * 2 - 10;
        } else if (shape.id.startsWith("ellipse-")) {
          textX = shapePosition.x - shape.radiusX + 5;
          textY = shapePosition.y - shape.radiusY + 5;
          textWidth = shape.radiusX * 2 - 10;
          textHeight = shape.radiusY * 2 - 10;
        } else if (shape.id.startsWith("triangle-")) {
          const size = shape.size || 80;
          textX = shapePosition.x - size / 2 + 5;
          textY = shapePosition.y - size / 2 + 5;
          textWidth = size - 10;
          textHeight = size - 10;
        } else if (shape.id.startsWith("star-")) {
          // Star bounding box (approximate)
          const maxRadius = Math.max(...shape.points.filter((_, i) => i % 2 === 0).map((p, i) => Math.abs(p)));
          textX = shapePosition.x - maxRadius + 5;
          textY = shapePosition.y - maxRadius + 5;
          textWidth = maxRadius * 2 - 10;
          textHeight = maxRadius * 2 - 10;
        } else {
          // Default for other shapes
          textX = shapePosition.x + 5;
          textY = shapePosition.y + 5;
          textWidth = 100;
          textHeight = 50;
        }

        const areaPosition = {
          x: stageBox.left + textX,
          y: stageBox.top + textY,
        };

        textInputRef.current.style.position = 'fixed';
        textInputRef.current.style.left = `${areaPosition.x}px`;
        textInputRef.current.style.top = `${areaPosition.y}px`;
        textInputRef.current.style.width = `${Math.max(50, textWidth)}px`;
        textInputRef.current.style.height = `${Math.max(20, textHeight)}px`;
        textInputRef.current.style.fontSize = `${shape.textFontSize || 24}px`;
        textInputRef.current.style.border = '2px solid #007bff';
        textInputRef.current.style.padding = '4px';
        textInputRef.current.style.margin = '0px';
        textInputRef.current.style.overflow = 'auto';
        textInputRef.current.style.background = 'rgba(255, 255, 255, 0.95)';
        textInputRef.current.style.outline = 'none';
        textInputRef.current.style.color = shape.textFill || '#000000';
        textInputRef.current.style.fontFamily = shape.textFontFamily || 'Arial';
        textInputRef.current.style.fontStyle = shape.textFontStyle || 'normal';
        textInputRef.current.style.textDecoration = shape.textDecoration || 'none';
        textInputRef.current.style.transform = `rotateZ(${groupNode.rotation() || 0}deg)`;
        textInputRef.current.style.zIndex = '10000';
        textInputRef.current.style.textAlign = 'center';
        textInputRef.current.style.display = 'block';
        textInputRef.current.style.whiteSpace = 'pre-wrap';
        textInputRef.current.style.wordWrap = 'break-word';
        textInputRef.current.style.resize = 'both';
        textInputRef.current.value = shape.text || "";
        textInputRef.current.focus();
        textInputRef.current.select();
      } catch (error) {
        console.error("Error setting up text input:", error);
      }
    }, 100);
  };

  // Handle double click to edit text (for text elements and shapes)
  const handleTextDblClick = (e) => {
    e.cancelBubble = true;
    if (e.evt) {
      e.evt.cancelBubble = true;
    }

    const node = e.target;
    const stage = node.getStage();
    if (!stage) return;

    const container = stage.container();
    if (!container) return;

    // Check if clicked on a shape inside a Group
    let shapeId = null;
    const clickedNode = node;
    const parentGroup = clickedNode.getParent();

    // If parent is a Group, use its ID (shapes are in Groups)
    if (parentGroup && parentGroup.className === 'Group' && parentGroup.id()) {
      shapeId = parentGroup.id();
    } else {
      // Check if the node itself has an ID (for text elements)
      shapeId = clickedNode.id();
    }

    if (!shapeId) return;

    // Check if it's a shape
    const shape = shapes.find((s) => s.id === shapeId);

    if (shape) {
      startEditingShapeText(shapeId);
      return;
    }

    // Handle regular text elements
    const textNode = clickedNode;
    setEditingId(shapeId);
    setEditingText(textNode.text() || "");

    // Use setTimeout to ensure DOM is updated before accessing ref
    setTimeout(() => {
      if (!textInputRef.current) return;

      try {
        const textPosition = textNode.absolutePosition();
        const stageBox = container.getBoundingClientRect();

        const areaPosition = {
          x: stageBox.left + textPosition.x,
          y: stageBox.top + textPosition.y,
        };

        const padding = textNode.padding() || 0;
        const width = Math.max(50, textNode.width() - padding * 2);
        const height = Math.max(20, textNode.height() - padding * 2);

        textInputRef.current.style.position = 'fixed';
        textInputRef.current.style.left = `${areaPosition.x}px`;
        textInputRef.current.style.top = `${areaPosition.y}px`;
        textInputRef.current.style.width = `${width}px`;
        textInputRef.current.style.height = `${height}px`;
        textInputRef.current.style.fontSize = `${textNode.fontSize() || 24}px`;
        textInputRef.current.style.border = '2px solid #007bff';
        textInputRef.current.style.padding = '4px';
        textInputRef.current.style.margin = '0px';
        textInputRef.current.style.overflow = 'auto';
        textInputRef.current.style.background = 'rgba(255, 255, 255, 0.95)';
        textInputRef.current.style.outline = 'none';
        textInputRef.current.style.color = textNode.fill() || '#000000';
        textInputRef.current.style.fontFamily = textNode.fontFamily() || 'Arial';
        textInputRef.current.style.fontStyle = textNode.fontStyle() || 'normal';
        textInputRef.current.style.textDecoration = textNode.textDecoration() || 'none';
        textInputRef.current.style.transform = `rotateZ(${textNode.rotation() || 0}deg)`;
        textInputRef.current.style.zIndex = '10000';
        textInputRef.current.value = textNode.text() || "";
        textInputRef.current.focus();
        textInputRef.current.select();
      } catch (error) {
        console.error("Error setting up text input:", error);
      }
    }, 100);
  };

  // Handle text input change
  const handleTextInputChange = () => {
    if (!textInputRef.current || !editingId) return;
    const id = editingId;

    // Check if editing a shape
    const shape = shapes.find((s) => s.id === id);
    if (shape) {
      setShapes((prev) =>
        prev.map((el) => {
          if (el.id === id) {
            return { ...el, text: textInputRef.current.value };
          }
          return el;
        })
      );
      return;
    }

    // Handle regular text elements
    setTextElements((prev) =>
      prev.map((el) => {
        if (el.id === id) {
          return { ...el, text: textInputRef.current.value };
        }
        return el;
      })
    );
  };

  // Handle text input blur
  const handleTextInputBlur = () => {
    setEditingId(null);
  };

  // Add text element
  const addTextElement = () => {
    const newText = {
      id: `text-${Date.now()}`,
      x: 50,
      y: 50,
      text: "Double click to edit",
      fontSize: textFormat.fontSize,
      fontFamily: textFormat.fontFamily,
      fontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      fill: textFormat.fill,
      draggable: true,
      rotation: 0,
    };
    setTextElements([...textElements, newText]);
    setSelectedId(newText.id);
  };

  // Add rectangle
  const addRectangle = () => {
    const newRect = {
      id: `rect-${Date.now()}`,
      x: 50,
      y: 50,
      width: 100,
      height: 100,
      fill: "#3b82f6",
      stroke: "#1e40af",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
      rotation: 0,
    };
    setShapes([...shapes, newRect]);
    setSelectedId(newRect.id);
  };

  // Add circle
  const addCircle = () => {
    const newCircle = {
      id: `circle-${Date.now()}`,
      x: 100,
      y: 100,
      radius: 50,
      fill: "#ef4444",
      stroke: "#dc2626",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
      rotation: 0,
    };
    setShapes([...shapes, newCircle]);
    setSelectedId(newCircle.id);
  };

  // Add triangle
  const addTriangle = () => {
    const size = 80; // Base size for triangle
    const newTriangle = {
      id: `triangle-${Date.now()}`,
      x: 100,
      y: 100,
      size: size, // Size represents the height/base of the triangle
      fill: "#10b981",
      stroke: "#059669",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
      rotation: 0,
    };
    setShapes([...shapes, newTriangle]);
    setSelectedId(newTriangle.id);
  };

  const addEllipse = () => {
    const newEllipse = {
      id: `ellipse-${Date.now()}`,
      x: 100,
      y: 100,
      radiusX: 60,
      radiusY: 40,
      fill: "#8b5cf6",
      stroke: "#6d28d9",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
      rotation: 0,
    };
    setShapes([...shapes, newEllipse]);
    setSelectedId(newEllipse.id);
  };

  const addLine = () => {
    const newLine = {
      id: `line-${Date.now()}`,
      x: 50,
      y: 50,
      points: [0, 0, 100, 50], // [x1, y1, x2, y2] relative to x, y
      fill: "#000000",
      stroke: "#000000",
      strokeWidth: 3,
      draggable: true,
      rotation: 0,
    };
    setShapes([...shapes, newLine]);
    setSelectedId(newLine.id);
  };

  const addArrow = () => {
    const arrowLength = 80;
    const arrowHeadSize = 15;
    // Arrow points: line from start to end, then arrowhead triangle
    const newArrow = {
      id: `arrow-${Date.now()}`,
      x: 50,
      y: 50,
      points: [
        0, 0, // Start point
        arrowLength, 0, // End point (line)
        arrowLength - arrowHeadSize, -arrowHeadSize / 2, // Arrowhead point 1
        arrowLength, 0, // Back to end
        arrowLength - arrowHeadSize, arrowHeadSize / 2, // Arrowhead point 2
      ],
      fill: "#f59e0b",
      stroke: "#d97706",
      strokeWidth: 2,
      draggable: true,
      rotation: 0,
    };
    setShapes([...shapes, newArrow]);
    setSelectedId(newArrow.id);
  };

  const addStar = () => {
    const outerRadius = 40;
    const innerRadius = 20;
    const numPoints = 5;
    const points = [];

    for (let i = 0; i < numPoints * 2; i++) {
      const angle = (i * Math.PI) / numPoints;
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      points.push(Math.cos(angle) * radius);
      points.push(Math.sin(angle) * radius);
    }

    const newStar = {
      id: `star-${Date.now()}`,
      x: 100,
      y: 100,
      points: points,
      fill: "#fbbf24",
      stroke: "#f59e0b",
      strokeWidth: 2,
      draggable: true,
      text: "",
      textFontSize: textFormat.fontSize,
      textFontFamily: textFormat.fontFamily,
      textFontStyle: textFormat.fontStyle,
      textDecoration: textFormat.textDecoration,
      textFill: "#0e0d0d",
      rotation: 0,
    };
    setShapes([...shapes, newStar]);
    setSelectedId(newStar.id);
  };

  // Delete selected element
  const deleteSelected = async () => {
    if (!selectedId) return;

    const confirmed = await showDeleteConfirm({
      title: "Remove element?",
      text: "Are you sure you want to remove the selected element from this template?",
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
    });
    if (!confirmed) return;

    setTextElements((prev) => prev.filter((el) => el.id !== selectedId));
    setShapes((prev) => prev.filter((el) => el.id !== selectedId));
    setSelectedId(null);
  };

  // Update selected element formatting
  const updateSelectedFormat = (format) => {
    if (!selectedId) return;

    const isText = textElements.find((el) => el.id === selectedId);
    if (isText) {
      setTextElements((prev) =>
        prev.map((el) => {
          if (el.id === selectedId) {
            return { ...el, ...format };
          }
          return el;
        })
      );
      setTextFormat((prev) => ({ ...prev, ...format }));
      return;
    }

    // Handle shape text formatting
    const shape = shapes.find((el) => el.id === selectedId);
    if (shape) {
      const shapeFormat = {};
      Object.keys(format).forEach((key) => {
        if (key === 'fontSize') {
          shapeFormat.textFontSize = format.fontSize;
        } else if (key === 'fontFamily') {
          shapeFormat.textFontFamily = format.fontFamily;
        } else if (key === 'fontStyle') {
          shapeFormat.textFontStyle = format.fontStyle;
        } else if (key === 'textDecoration') {
          shapeFormat.textDecoration = format.textDecoration;
        } else if (key === 'fill') {
          shapeFormat.textFill = format.fill;
        } else if (key === 'shapeFill') {
          // Handle shape background/fill color separately
          shapeFormat.fill = format.shapeFill;
        } else {
          shapeFormat[key] = format[key];
        }
      });

      setShapes((prev) =>
        prev.map((el) => {
          if (el.id === selectedId) {
            return { ...el, ...shapeFormat };
          }
          return el;
        })
      );
    }
  };

  // Function to update shape background/fill color
  const updateShapeFillColor = (color) => {
    if (!selectedId) return;

    const shape = shapes.find((el) => el.id === selectedId);
    if (shape) {
      setShapes((prev) =>
        prev.map((el) => {
          if (el.id === selectedId) {
            // Line shapes use stroke instead of fill
            if (el.id.startsWith("line-")) {
              return { ...el, stroke: color };
            }
            // All other shapes use fill
            return { ...el, fill: color };
          }
          return el;
        })
      );
    }
  };

  // Toggle bold
  const toggleBold = () => {
    const selectedText = textElements.find((el) => el.id === selectedId);
    const selectedShape = shapes.find((el) => el.id === selectedId);

    if (selectedText) {
      const newStyle = selectedText.fontStyle.includes("bold")
        ? selectedText.fontStyle.replace("bold", "").trim()
        : selectedText.fontStyle + " bold";
      updateSelectedFormat({ fontStyle: newStyle || "normal" });
    } else if (selectedShape) {
      const currentStyle = selectedShape.textFontStyle || "normal";
      const newStyle = currentStyle.includes("bold")
        ? currentStyle.replace("bold", "").trim()
        : currentStyle + " bold";
      updateSelectedFormat({ fontStyle: newStyle || "normal" });
    }
  };

  // Toggle italic
  const toggleItalic = () => {
    const selectedText = textElements.find((el) => el.id === selectedId);
    const selectedShape = shapes.find((el) => el.id === selectedId);

    if (selectedText) {
      const newStyle = selectedText.fontStyle.includes("italic")
        ? selectedText.fontStyle.replace("italic", "").trim()
        : selectedText.fontStyle + " italic";
      updateSelectedFormat({ fontStyle: newStyle || "normal" });
    } else if (selectedShape) {
      const currentStyle = selectedShape.textFontStyle || "normal";
      const newStyle = currentStyle.includes("italic")
        ? currentStyle.replace("italic", "").trim()
        : currentStyle + " italic";
      updateSelectedFormat({ fontStyle: newStyle || "normal" });
    }
  };

  // Toggle underline
  const toggleUnderline = () => {
    const selectedText = textElements.find((el) => el.id === selectedId);
    const selectedShape = shapes.find((el) => el.id === selectedId);

    if (selectedText) {
      const newDecoration = selectedText.textDecoration === "underline" ? "" : "underline";
      updateSelectedFormat({ textDecoration: newDecoration });
    } else if (selectedShape) {
      const newDecoration = selectedShape.textDecoration === "underline" ? "" : "underline";
      updateSelectedFormat({ textDecoration: newDecoration });
    }
  };

  // Update transformer when selection changes
  useEffect(() => {
    if (selectedId && transformerRef.current) {
      const stage = transformerRef.current.getStage();
      const selectedNode = stage.findOne(`#${selectedId}`);
      if (selectedNode) {
        // For shapes in Groups, attach transformer to the Group
        const nodeToTransform = selectedNode.getParent()?.className === 'Group'
          ? selectedNode.getParent()
          : selectedNode;
        transformerRef.current.nodes([nodeToTransform]);
        transformerRef.current.getLayer().batchDraw();
      }
    } else {
      transformerRef.current?.nodes([]);
    }
  }, [selectedId, textElements, shapes]);

  // Convert Konva elements to HTML - get actual positions from Konva nodes
  const convertToHTML = () => {
    const imageWidth = imageDimensions.width;
    const imageHeight = imageDimensions.height;
    const stage = stageRef.current;

    let html = "";

    // Convert text elements - get actual positions from Konva
    textElements.forEach((el) => {
      let x = el.x;
      let y = el.y;
      let rotation = el.rotation || 0;

      // Try to get actual position and rotation from Konva node if available
      if (stage) {
        const node = stage.findOne(`#${el.id}`);
        if (node) {
          const pos = node.absolutePosition();
          x = pos.x;
          y = pos.y;
          rotation = node.rotation() || 0;
        }
      }

      const leftPercent = (x / imageWidth) * 100;
      const topPercent = (y / imageHeight) * 100;

      const style = `
        position: absolute;
        left: ${leftPercent}%;
        top: ${topPercent}%;
        font-size: ${el.fontSize}px;
        font-family: ${el.fontFamily};
        font-style: ${el.fontStyle.includes("italic") ? "italic" : "normal"};
        font-weight: ${el.fontStyle.includes("bold") ? "bold" : "normal"};
        text-decoration: ${el.textDecoration || "none"};
        color: ${el.fill};
        white-space: pre-wrap;
        word-wrap: break-word;
        transform: rotate(${rotation}deg);
        transform-origin: center center;
      `;

      html += `<div style="${style}">${el.text}</div>`;
    });

    // Convert shapes (as divs with borders and text) - get actual positions from Konva
    shapes.forEach((el) => {
      let x = el.x;
      let y = el.y;
      let width = el.width;
      let height = el.height;
      let radius = el.radius;
      let rotation = el.rotation || 0;

      // Try to get actual position from Konva node if available
      if (stage) {
        const node = stage.findOne(`#${el.id}`);
        if (node) {
          const pos = node.absolutePosition();
          x = pos.x;
          y = pos.y;
          rotation = node.rotation() || 0;

          // Check if it's a Group node (most shapes are in Groups)
          if (node.className === 'Group' && typeof node.getChildren === 'function') {
            const children = node.getChildren();
            const shapeNode = children.find(child =>
              child.className === 'Rect' || child.className === 'Circle' || child.className === 'Line' || child.className === 'Ellipse'
            );

            if (shapeNode) {
              if (shapeNode.className === 'Rect') {
                width = shapeNode.width();
                height = shapeNode.height();
              } else if (shapeNode.className === 'Circle') {
                radius = shapeNode.radius();
              } else if (shapeNode.className === 'Ellipse') {
                // Ellipse dimensions are handled separately in ellipse conversion
              } else if (shapeNode.className === 'Line' && el.id.startsWith("triangle-")) {
                // For triangles, size is stored in the shape object
              }
            }
          } else if (node.className === 'Line' && el.id.startsWith("line-")) {
            // Line shapes are not in Groups, handle directly
            // Points are already stored in el.points
          }
        }
      }

      if (el.id.startsWith("rect-")) {
        const leftPercent = (x / imageWidth) * 100;
        const topPercent = (y / imageHeight) * 100;
        const widthPercent = (width / imageWidth) * 100;
        const heightPercent = (height / imageHeight) * 100;

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          transform: rotate(${rotation}deg);
          transform-origin: center center;
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      } else if (el.id.startsWith("circle-")) {
        const leftPercent = ((x - radius) / imageWidth) * 100;
        const topPercent = ((y - radius) / imageHeight) * 100;
        const sizePercent = ((radius * 2) / imageWidth) * 100;

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${sizePercent}%;
          height: ${sizePercent}%;
          border-radius: 50%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          transform: rotate(${rotation}deg);
          transform-origin: center center;
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      } else if (el.id.startsWith("triangle-")) {
        const size = el.size || 80;
        const leftPercent = ((x - size / 2) / imageWidth) * 100;
        const topPercent = ((y - size / 2) / imageHeight) * 100;
        const sizePercent = (size / imageWidth) * 100;

        // Create triangle using CSS clip-path
        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${sizePercent}%;
          height: ${sizePercent}%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          transform: rotate(${rotation}deg);
          transform-origin: center center;
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
          margin-top: 20%;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      } else if (el.id.startsWith("ellipse-")) {
        let radiusX = el.radiusX;
        let radiusY = el.radiusY;
        let ellipseRotation = rotation;

        if (stage) {
          const groupNode = stage.findOne(`#${el.id}`);
          if (groupNode && groupNode.className === 'Group' && typeof groupNode.findOne === 'function') {
            ellipseRotation = groupNode.rotation() || 0;
            const ellipseNode = groupNode.findOne("Ellipse");
            if (ellipseNode) {
              radiusX = ellipseNode.radiusX();
              radiusY = ellipseNode.radiusY();
            }
          }
        }

        const leftPercent = ((x - radiusX) / imageWidth) * 100;
        const topPercent = ((y - radiusY) / imageHeight) * 100;
        const widthPercent = ((radiusX * 2) / imageWidth) * 100;
        const heightPercent = ((radiusY * 2) / imageHeight) * 100;

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          border-radius: 50%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          transform: rotate(${ellipseRotation}deg);
          transform-origin: center center;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      } else if (el.id.startsWith("line-")) {
        // Convert line to SVG path
        let linePoints = el.points || [0, 0, 100, 50];
        let lineRotation = rotation;

        // Get actual points and rotation from Konva node if available
        if (stage) {
          const lineNode = stage.findOne(`#${el.id}`);
          if (lineNode && lineNode.className === 'Line') {
            linePoints = lineNode.points() || linePoints;
            lineRotation = lineNode.rotation() || 0;
          }
        }

        const startX = x + linePoints[0];
        const startY = y + linePoints[1];
        const endX = x + linePoints[2];
        const endY = y + linePoints[3];

        const leftPercent = (Math.min(startX, endX) / imageWidth) * 100;
        const topPercent = (Math.min(startY, endY) / imageHeight) * 100;
        const widthPercent = (Math.abs(endX - startX) / imageWidth) * 100;
        const heightPercent = (Math.abs(endY - startY) / imageHeight) * 100;

        const svgPath = `M ${linePoints[0]} ${linePoints[1]} L ${linePoints[2]} ${linePoints[3]}`;
        const svgWidth = Math.abs(linePoints[2] - linePoints[0]) || 100;
        const svgHeight = Math.abs(linePoints[3] - linePoints[1]) || 50;

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          transform: rotate(${lineRotation}deg);
          transform-origin: top left;
        `;

        html += `<div style="${style}"><svg width="${svgWidth}" height="${svgHeight}" style="position: absolute; top: 0; left: 0;"><path d="${svgPath}" stroke="${el.stroke}" stroke-width="${el.strokeWidth}" fill="none" /></svg></div>`;
      } else if (el.id.startsWith("arrow-")) {
        // Convert arrow to SVG
        let arrowPoints = el.points || [];
        let arrowRotation = rotation;

        // Get actual points and rotation from Konva Group if available
        if (stage) {
          const groupNode = stage.findOne(`#${el.id}`);
          if (groupNode && groupNode.className === 'Group') {
            arrowRotation = groupNode.rotation() || 0;
            const lineNode = groupNode.findOne("Line");
            if (lineNode) {
              arrowPoints = lineNode.points() || arrowPoints;
            }
          }
        }

        if (arrowPoints.length === 0) return;

        const maxX = Math.max(...arrowPoints.filter((_, i) => i % 2 === 0));
        const maxY = Math.max(...arrowPoints.filter((_, i) => i % 2 === 1));
        const minX = Math.min(...arrowPoints.filter((_, i) => i % 2 === 0));
        const minY = Math.min(...arrowPoints.filter((_, i) => i % 2 === 1));

        const leftPercent = ((x + minX) / imageWidth) * 100;
        const topPercent = ((y + minY) / imageHeight) * 100;
        const widthPercent = ((maxX - minX) / imageWidth) * 100;
        const heightPercent = ((maxY - minY) / imageHeight) * 100;

        // Generate SVG path from points array [x1, y1, x2, y2, ...]
        let pathData = '';
        for (let i = 0; i < arrowPoints.length; i += 2) {
          if (i === 0) {
            pathData += `M ${arrowPoints[i]} ${arrowPoints[i + 1]}`;
          } else {
            pathData += ` L ${arrowPoints[i]} ${arrowPoints[i + 1]}`;
          }
        }
        pathData += ' Z';

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          transform: rotate(${arrowRotation}deg);
          transform-origin: center center;
        `;

        html += `<div style="${style}"><svg width="${maxX - minX}" height="${maxY - minY}" viewBox="${minX} ${minY} ${maxX - minX} ${maxY - minY}"><path d="${pathData}" fill="${el.fill}" stroke="${el.stroke}" stroke-width="${el.strokeWidth}" /></svg></div>`;
      } else if (el.id.startsWith("star-")) {
        // Convert star to SVG
        let starPoints = el.points || [];
        let starRotation = rotation;

        // Get actual points and rotation from Konva Group if available
        if (stage) {
          const groupNode = stage.findOne(`#${el.id}`);
          if (groupNode && groupNode.className === 'Group') {
            starRotation = groupNode.rotation() || 0;
            const lineNode = groupNode.findOne("Line");
            if (lineNode) {
              starPoints = lineNode.points() || starPoints;
            }
          }
        }

        if (starPoints.length === 0) return;

        const maxX = Math.max(...starPoints.filter((_, i) => i % 2 === 0));
        const maxY = Math.max(...starPoints.filter((_, i) => i % 2 === 1));
        const minX = Math.min(...starPoints.filter((_, i) => i % 2 === 0));
        const minY = Math.min(...starPoints.filter((_, i) => i % 2 === 1));

        const leftPercent = ((x + minX) / imageWidth) * 100;
        const topPercent = ((y + minY) / imageHeight) * 100;
        const widthPercent = ((maxX - minX) / imageWidth) * 100;
        const heightPercent = ((maxY - minY) / imageHeight) * 100;

        // Generate SVG path from points array [x1, y1, x2, y2, ...]
        let pathData = '';
        for (let i = 0; i < starPoints.length; i += 2) {
          if (i === 0) {
            pathData += `M ${starPoints[i]} ${starPoints[i + 1]}`;
          } else {
            pathData += ` L ${starPoints[i]} ${starPoints[i + 1]}`;
          }
        }
        pathData += ' Z';

        const style = `
          position: absolute;
          left: ${leftPercent}%;
          top: ${topPercent}%;
          width: ${widthPercent}%;
          height: ${heightPercent}%;
          background-color: ${el.fill};
          border: ${el.strokeWidth}px solid ${el.stroke};
          transform: rotate(${starRotation}deg);
          transform-origin: center center;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          clip-path: polygon(${starPoints.filter((_, i) => i % 2 === 0).map((px, idx) => {
          const py = starPoints[idx * 2 + 1];
          const relX = ((px - minX) / (maxX - minX || 1)) * 100;
          const relY = ((py - minY) / (maxY - minY || 1)) * 100;
          return `${relX.toFixed(2)}% ${relY.toFixed(2)}%`;
        }).join(', ')});
        `;

        const textStyle = `
          font-size: ${el.textFontSize || 24}px;
          font-family: ${el.textFontFamily || "Arial"};
          font-style: ${el.textFontStyle?.includes("italic") ? "italic" : "normal"};
          font-weight: ${el.textFontStyle?.includes("bold") ? "bold" : "normal"};
          text-decoration: ${el.textDecoration || "none"};
          color: ${el.textFill || "#ffffff"};
          white-space: pre-wrap;
          word-wrap: break-word;
          text-align: center;
        `;

        html += `<div style="${style}"><span style="${textStyle}">${el.text || ""}</span></div>`;
      }
    });

    return html;
  };

  const handleSave = async () => {
    if (!templateName || !templateCategory) {
      await showWarningAlert(
        "Missing Information",
        "Please fill in template name and category.",
      );
      return;
    }

    if (!isFromSample && !isEditMode && !bgImageFile) {
      await showWarningAlert(
        "Image Required",
        "Please upload a background image.",
      );
      return;
    }

    if (textElements.length === 0 && shapes.length === 0) {
      await showWarningAlert(
        "Empty Template",
        "Please add at least one text element or shape.",
      );
      return;
    }

    try {
      const htmlContent = convertToHTML();
      const encodedCanvasState = encodeCanvasState({
        version: 1,
        baseWidth: imageDimensions.width,
        baseHeight: imageDimensions.height,
        textElements,
        shapes,
      });

      const rootCanvasStateAttr = encodedCanvasState
        ? ` data-canvas-state="${encodedCanvasState}"`
        : "";

      const finalHtml = `<div${rootCanvasStateAttr} style="position: relative; width: 100%; height: 100%; margin: 0; padding: 0; box-sizing: border-box;">${htmlContent}</div>${
        encodedCanvasState
          ? `<!--${CANVAS_STATE_COMMENT_PREFIX}${encodedCanvasState}-->`
          : ""
      }`;

      let imageFileToUpload = bgImageFile;

      if (
        !imageFileToUpload &&
        (isFromSample || isEditMode) &&
        sampleImageUrlRef.current
      ) {
        const response = await fetch(sampleImageUrlRef.current);
        const blob = await response.blob();
        const fallbackName =
          loadTemplate?.templateImage?.split("/").pop() || "template-image.png";
        imageFileToUpload = new File([blob], fallbackName, {
          type: blob.type || "image/png",
        });
      }

      if (!imageFileToUpload && !isEditMode) {
        await showWarningAlert(
          "Image Required",
          "A background image is required to save this template.",
        );
        return;
      }

      const formData = new FormData();
      formData.append("Name", templateName);
      formData.append("TemplateBody", finalHtml);
      formData.append("TemplateDetails", editTemplate?.templateDetails || "test");
      formData.append("TemplateCategoryId", getCategoryId(templateCategory));

      if (imageFileToUpload) {
        formData.append("TemplateImage", imageFileToUpload);
      }

      if (isEditMode) {
        formData.append("Id", editTemplate.id);
        formData.append("IsActive", editTemplate.active ?? true);
        await TemplateService.updateTemplate(formData);
        await showSuccessAlert("Updated", "Template updated successfully!");
      } else {
        if (!imageFileToUpload) {
          await showWarningAlert(
            "Image Required",
            "A background image is required to save this template.",
          );
          return;
        }
        await TemplateService.createTemplate(formData);
        await showSuccessAlert("Saved", "Template saved successfully!");
      }
      onBack();
    } catch (error) {
      console.error(error);
      await showErrorAlert(
        "Save Failed",
        error?.response?.data?.message || "Error saving template.",
      );
    }
  };

  const selectedElement = textElements.find((el) => el.id === selectedId) || shapes.find((el) => el.id === selectedId);

  return (
    <Row className="p-4">
      {/* Preview Section */}
      <Col md={6} className="mb-4">
        <div className="border rounded p-3">
          <div style={{ marginBottom: "10px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            {/* Add Text Button */}
            <button
              onClick={addTextElement}
              style={{
                padding: "8px 12px",
                border: "2px solid #007bff",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#007bff",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#007bff";
                e.target.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                e.target.style.color = "#007bff";
              }}
            >
              <span style={{ fontSize: "16px" }}>T</span>
              <span>Text</span>
            </button>

            {/* Add Rectangle Button */}
            <button
              onClick={addRectangle}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #3b82f6",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#3b82f6";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#3b82f6";
                }
              }}
              title="Add Rectangle"
            >
              <div
                className="shape-icon"
                style={{
                  width: "24px",
                  height: "18px",
                  border: "2px solid #3b82f6",
                  backgroundColor: "transparent",
                  transition: "all 0.2s",
                }}
              />
            </button>

            {/* Add Circle Button */}
            <button
              onClick={addCircle}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #ef4444",
                borderRadius: "50%",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#ef4444";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#ef4444";
                }
              }}
              title="Add Circle"
            >
              <div
                className="shape-icon"
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #ef4444",
                  borderRadius: "50%",
                  backgroundColor: "transparent",
                  transition: "all 0.2s",
                }}
              />
            </button>

            {/* Add Triangle Button */}
            <button
              onClick={addTriangle}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #10b981",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#10b981";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#10b981";
                }
              }}
              title="Add Triangle"
            >
              <svg
                width="20"
                height="18"
                viewBox="0 0 20 18"
                style={{ pointerEvents: "none" }}
              >
                <path
                  d="M10 0 L20 18 L0 18 Z"
                  fill="transparent"
                  stroke="#10b981"
                  strokeWidth="2"
                  style={{ transition: "all 0.2s" }}
                />
              </svg>
            </button>

            {/* Add Ellipse Button */}
            <button
              onClick={addEllipse}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #8b5cf6",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#8b5cf6";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const shape = e.target.querySelector('.shape-icon');
                if (shape) {
                  shape.style.borderColor = "#8b5cf6";
                }
              }}
              title="Add Ellipse"
            >
              <div
                className="shape-icon"
                style={{
                  width: "24px",
                  height: "16px",
                  border: "2px solid #8b5cf6",
                  borderRadius: "50%",
                  backgroundColor: "transparent",
                  transition: "all 0.2s",
                }}
              />
            </button>

            {/* Add Line Button */}
            <button
              onClick={addLine}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #6b7280",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#6b7280";
                const line = e.target.querySelector('.line-icon');
                if (line) {
                  line.style.backgroundColor = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const line = e.target.querySelector('.line-icon');
                if (line) {
                  line.style.backgroundColor = "#6b7280";
                }
              }}
              title="Add Line"
            >
              <div
                className="line-icon"
                style={{
                  width: "20px",
                  height: "3px",
                  backgroundColor: "#6b7280",
                  transition: "all 0.2s",
                }}
              />
            </button>

            {/* Add Arrow Button */}
            <button
              onClick={addArrow}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #f59e0b",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f59e0b";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#fff";
                  path.style.fill = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#f59e0b";
                  path.style.fill = "#f59e0b";
                }
              }}
              title="Add Arrow"
            >
              <svg
                width="20"
                height="18"
                viewBox="0 0 20 18"
                style={{ pointerEvents: "none" }}
              >
                <path
                  d="M0 9 L15 9 M15 9 L10 4 M15 9 L10 14"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  style={{ transition: "all 0.2s" }}
                />
              </svg>
            </button>

            {/* Add Star Button */}
            <button
              onClick={addStar}
              className="shape-button"
              style={{
                padding: "8px 12px",
                border: "2px solid #fbbf24",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#fbbf24";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#fff";
                  path.style.fill = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#fff";
                const path = e.target.querySelector('path');
                if (path) {
                  path.style.stroke = "#fbbf24";
                  path.style.fill = "#fbbf24";
                }
              }}
              title="Add Star"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                style={{ pointerEvents: "none" }}
              >
                <path
                  d="M10 0 L12.5 7.5 L20 7.5 L14 12.5 L16.5 20 L10 15 L3.5 20 L6 12.5 L0 7.5 L7.5 7.5 Z"
                  fill="#fbbf24"
                  stroke="#fbbf24"
                  strokeWidth="1"
                  style={{ transition: "all 0.2s" }}
                />
              </svg>
            </button>

            {/* Delete Button */}
            {selectedId && (
              <button
                onClick={deleteSelected}
                style={{
                  padding: "8px 12px",
                  border: "2px solid #dc3545",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#dc3545",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#dc3545";
                  e.target.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#fff";
                  e.target.style.color = "#dc3545";
                }}
              >
                <span>Delete</span>
              </button>
            )}
          </div>

          {/* Text Formatting Toolbar */}
          {selectedElement && (textElements.find((el) => el.id === selectedId) || shapes.find((el) => el.id === selectedId)) && (
            <div className="mb-3 p-2 border rounded">
              {/* Edit Text Button for Shapes */}
              {shapes.find((el) => el.id === selectedId) && (
                <div className="mb-2">
                  <PrimaryButton
                    className="px-3 py-1"
                    style={{ fontSize: "0.875rem" }}
                    onClick={() => startEditingShapeText(selectedId)}
                  >
                    Edit Text
                  </PrimaryButton>
                </div>
              )}

              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <Button
                  variant={
                    (selectedElement.fontStyle?.includes("bold") || selectedElement.textFontStyle?.includes("bold"))
                      ? "primary"
                      : "outline-secondary"
                  }
                  size="sm"
                  onClick={toggleBold}
                >
                  <strong>B</strong>
                </Button>
                <Button
                  variant={
                    (selectedElement.fontStyle?.includes("italic") || selectedElement.textFontStyle?.includes("italic"))
                      ? "primary"
                      : "outline-secondary"
                  }
                  size="sm"
                  onClick={toggleItalic}
                >
                  <em>I</em>
                </Button>
                <Button
                  variant={
                    (selectedElement.textDecoration === "underline")
                      ? "primary"
                      : "outline-secondary"
                  }
                  size="sm"
                  onClick={toggleUnderline}
                >
                  <u>U</u>
                </Button>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2">
                <Form.Label className="mb-0">Size:</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  style={{ width: "80px" }}
                  value={selectedElement.fontSize || selectedElement.textFontSize || textFormat.fontSize}
                  onChange={(e) => updateSelectedFormat({ fontSize: parseInt(e.target.value) || 24 })}
                />

                <Form.Label className="mb-0">Font:</Form.Label>
                <Form.Select
                  size="sm"
                  style={{ width: "150px" }}
                  value={selectedElement.fontFamily || selectedElement.textFontFamily || textFormat.fontFamily}
                  onChange={(e) => updateSelectedFormat({ fontFamily: e.target.value })}
                >
                  <option value="Arial">Arial</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Comic Sans MS">Comic Sans MS</option>
                </Form.Select>

                <Form.Label className="mb-0">Font Color:</Form.Label>
                <Form.Control
                  type="color"
                  size="sm"
                  style={{ width: "60px", height: "31px" }}
                  value={selectedElement.fill || selectedElement.textFill || textFormat.fill}
                  onChange={(e) => updateSelectedFormat({ fill: e.target.value })}
                />
              </div>

              {/* Shape Background Color (only for shapes) */}
              {shapes.find((el) => el.id === selectedId) && (
                <div className="d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top">
                  <Form.Label className="mb-0">
                    {shapes.find((el) => el.id === selectedId)?.id.startsWith("line-")
                      ? "Line Color:"
                      : "Shape Color:"}
                  </Form.Label>
                  <Form.Control
                    type="color"
                    size="sm"
                    style={{ width: "60px", height: "31px" }}
                    value={
                      shapes.find((el) => el.id === selectedId)?.id.startsWith("line-")
                        ? (shapes.find((el) => el.id === selectedId)?.stroke || "#000000")
                        : (shapes.find((el) => el.id === selectedId)?.fill || "#3b82f6")
                    }
                    onChange={(e) => updateShapeFillColor(e.target.value)}
                  />
                  <span className="text-muted small">
                    {shapes.find((el) => el.id === selectedId)?.id.startsWith("line-")
                      ? (shapes.find((el) => el.id === selectedId)?.stroke || "#000000")
                      : (shapes.find((el) => el.id === selectedId)?.fill || "#3b82f6")}
                  </span>
                </div>
              )}
            </div>
          )}

          <div
            style={{
              position: "relative",
              display: "inline-block",
              border: "1px solid #ddd",
              backgroundColor: "#fff",
            }}
          >
            <Stage
              width={imageDimensions.width}
              height={imageDimensions.height}
              ref={stageRef}
              onClick={handleStageClick}
              onTap={handleStageClick}
            >
              <Layer>
                {/* Background Image */}
                {bgKonvaImage && (
                  <KonvaImage
                    x={0}
                    y={0}
                    width={imageDimensions.width}
                    height={imageDimensions.height}
                    image={bgKonvaImage}
                  />
                )}

                {/* Text Elements */}
                {textElements.map((el) => (
                  <Text
                    key={el.id}
                    id={el.id}
                    x={el.x}
                    y={el.y}
                    text={el.text}
                    fontSize={el.fontSize}
                    fontFamily={el.fontFamily}
                    fontStyle={el.fontStyle}
                    textDecoration={el.textDecoration}
                    fill={el.fill}
                    rotation={el.rotation}
                    draggable={el.draggable}
                    onDblClick={handleTextDblClick}
                    onDblTap={handleTextDblClick}
                    onDragEnd={(e) => {
                      setTextElements((prev) =>
                        prev.map((item) =>
                          item.id === el.id
                            ? { ...item, x: e.target.x(), y: e.target.y() }
                            : item
                        )
                      );
                    }}
                    onTransformEnd={(e) => {
                      const node = e.target;
                      setTextElements((prev) =>
                        prev.map((item) =>
                          item.id === el.id
                            ? { ...item, x: node.x(), y: node.y(), rotation: node.rotation() }
                            : item
                        )
                      );
                    }}
                  />
                ))}

                {/* Shapes */}
                {shapes.map((el) => {
                  if (el.id.startsWith("rect-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation}
                        draggable
                        onClick={() => setSelectedId(el.id)}
                        onDragEnd={(e) => {
                          const node = e.target;
                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? { ...item, x: node.x(), y: node.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          const scaleX = node.scaleX();
                          const scaleY = node.scaleY();

                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? {
                                  ...item,
                                  x: node.x(),
                                  y: node.y(),
                                  width: Math.max(5, el.width * scaleX),
                                  height: Math.max(5, el.height * scaleY),
                                  rotation: node.rotation(),
                                }
                                : item
                            )
                          );

                          node.scaleX(1);
                          node.scaleY(1);
                        }}
                      >
                        <Rect
                          width={el.width}
                          height={el.height}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                        />

                        {el.text && (
                          <Text
                            text={el.text}
                            width={el.width}
                            height={el.height}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>

                    );
                  } else if (el.id.startsWith("circle-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation}
                        draggable
                        onClick={() => setSelectedId(el.id)}
                        onDragEnd={(e) => {
                          const node = e.target;
                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? { ...item, x: node.x(), y: node.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          const scale = node.scaleX();

                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? {
                                  ...item,
                                  x: node.x(),
                                  y: node.y(),
                                  radius: Math.max(5, el.radius * scale),
                                  rotation: node.rotation(),
                                }
                                : item
                            )
                          );

                          node.scaleX(1);
                          node.scaleY(1);
                        }}
                      >
                        <Circle
                          radius={el.radius}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                        />

                        {el.text && (
                          <Text
                            text={el.text}
                            width={el.radius * 2}
                            height={el.radius * 2}
                            x={-el.radius}
                            y={-el.radius}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>

                    );
                  } else if (el.id.startsWith("triangle-")) {
                    const size = el.size || 80;
                    // Triangle points: top, bottom-left, bottom-right
                    const points = [
                      0, -size / 2,        // Top point
                      -size / 2, size / 2, // Bottom left
                      size / 2, size / 2   // Bottom right
                    ];

                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation}
                        draggable
                        onClick={() => setSelectedId(el.id)}
                        onDragEnd={(e) => {
                          const node = e.target;
                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? { ...item, x: node.x(), y: node.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          const scale = node.scaleX();

                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? {
                                  ...item,
                                  x: node.x(),
                                  y: node.y(),
                                  size: Math.max(10, el.size * scale),
                                  rotation: node.rotation(),
                                }
                                : item
                            )
                          );

                          node.scaleX(1);
                          node.scaleY(1);
                        }}
                      >
                        <Line
                          points={points}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          closed={true}
                          onDblClick={handleTextDblClick}
                          onDblTap={handleTextDblClick}
                        />

                        {el.text && (
                          <Text
                            text={el.text}
                            x={-size / 2}
                            y={-size / 4}
                            width={size}
                            height={size * 0.75}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>

                    );
                  } else if (el.id.startsWith("ellipse-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation}
                        draggable
                        onClick={() => setSelectedId(el.id)}
                        onDragEnd={(e) => {
                          const node = e.target;
                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? { ...item, x: node.x(), y: node.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          const scaleX = node.scaleX();
                          const scaleY = node.scaleY();

                          const ellipseNode = node.findOne("Ellipse");

                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? {
                                  ...item,
                                  x: node.x(),
                                  y: node.y(),
                                  radiusX: Math.max(5, ellipseNode.radiusX() * scaleX),
                                  radiusY: Math.max(5, ellipseNode.radiusY() * scaleY),
                                  rotation: node.rotation(),
                                }
                                : item
                            )
                          );

                          node.scaleX(1);
                          node.scaleY(1);
                        }}
                      >
                        <Ellipse
                          radiusX={el.radiusX}
                          radiusY={el.radiusY}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          onDblClick={handleTextDblClick}
                          onDblTap={handleTextDblClick}
                        />
                        {el.text && (
                          <Text
                            text={el.text}
                            x={-el.radiusX}
                            y={-el.radiusY}
                            width={el.radiusX * 2}
                            height={el.radiusY * 2}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>
                    );
                  } else if (el.id.startsWith("line-")) {
                    return (
                      <Line
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        points={el.points}
                        stroke={el.stroke}
                        strokeWidth={el.strokeWidth}
                        draggable={el.draggable}
                        rotation={el.rotation}
                        onClick={() => setSelectedId(el.id)}
                        onDragEnd={(e) => {
                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? { ...item, x: e.target.x(), y: e.target.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          const scaleX = node.scaleX();
                          const scaleY = node.scaleY();

                          setShapes(prev =>
                            prev.map(item => {
                              if (item.id === el.id) {
                                const newPoints = item.points.map((p, idx) => {
                                  if (idx % 2 === 0) return p * scaleX; // x
                                  return p * scaleY; // y
                                });
                                return {
                                  ...item,
                                  x: node.x(),
                                  y: node.y(),
                                  points: newPoints,
                                  rotation: node.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          node.scaleX(1);
                          node.scaleY(1);
                        }}
                      />
                    );
                  } else if (el.id.startsWith("arrow-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation}
                        draggable
                        onClick={() => setSelectedId(el.id)}
                        onDragEnd={(e) => {
                          const node = e.target;
                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? { ...item, x: node.x(), y: node.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          const scaleX = node.scaleX();
                          const scaleY = node.scaleY();
                          const scale = Math.max(scaleX, scaleY);

                          setShapes(prev =>
                            prev.map(item => {
                              if (item.id === el.id) {
                                return {
                                  ...item,
                                  x: node.x(),
                                  y: node.y(),
                                  points: item.points.map(p => p * scale),
                                  rotation: node.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          node.scaleX(1);
                          node.scaleY(1);
                        }}
                      >
                        <Line
                          points={el.points}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          closed={true}
                          onDblClick={handleTextDblClick}
                          onDblTap={handleTextDblClick}
                        />
                      </Group>
                    );
                  } else if (el.id.startsWith("star-")) {
                    return (
                      <Group
                        key={el.id}
                        id={el.id}
                        x={el.x}
                        y={el.y}
                        rotation={el.rotation}
                        draggable
                        onClick={() => setSelectedId(el.id)}
                        onDragEnd={(e) => {
                          const node = e.target;
                          setShapes(prev =>
                            prev.map(item =>
                              item.id === el.id
                                ? { ...item, x: node.x(), y: node.y() }
                                : item
                            )
                          );
                        }}
                        onTransformEnd={(e) => {
                          const node = e.target;
                          const scaleX = node.scaleX();
                          const scaleY = node.scaleY();
                          const scale = Math.max(scaleX, scaleY);

                          setShapes(prev =>
                            prev.map(item => {
                              if (item.id === el.id) {
                                return {
                                  ...item,
                                  x: node.x(),
                                  y: node.y(),
                                  points: item.points.map(p => p * scale),
                                  rotation: node.rotation(),
                                };
                              }
                              return item;
                            })
                          );

                          node.scaleX(1);
                          node.scaleY(1);
                        }}
                      >
                        <Line
                          points={el.points}
                          fill={el.fill}
                          stroke={el.stroke}
                          strokeWidth={el.strokeWidth}
                          closed={true}
                          onDblClick={handleTextDblClick}
                          onDblTap={handleTextDblClick}
                        />
                        {el.text && (
                          <Text
                            text={el.text}
                            x={-40}
                            y={-40}
                            width={80}
                            height={80}
                            align="center"
                            verticalAlign="middle"
                            fontSize={el.textFontSize || 24}
                            fontFamily={el.textFontFamily || "Arial"}
                            fontStyle={el.textFontStyle || "normal"}
                            textDecoration={el.textDecoration || ""}
                            fill={el.textFill || "#ffffff"}
                            listening={false}
                          />
                        )}
                      </Group>
                    );
                  }
                  return null;
                })}

                {/* Transformer for resizing/rotating */}
                <Transformer
                  ref={transformerRef}
                  boundBoxFunc={(oldBox, newBox) => {
                    if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />
              </Layer>
            </Stage>

            {/* Text input for editing - always rendered but hidden when not editing */}
            <textarea
              ref={textInputRef}
              value={editingText}
              onChange={(e) => {
                setEditingText(e.target.value);
                handleTextInputChange();
              }}
              onBlur={handleTextInputBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleTextInputBlur();
                }
                if (e.key === "Escape") {
                  handleTextInputBlur();
                }
              }}
              style={{
                position: "absolute",
                display: editingId ? "block" : "none",
                pointerEvents: editingId ? "auto" : "none",
              }}
            />
          </div>
        </div>
      </Col>

      {/* Form Section */}
      <Col md={6}>
        <h3>
          {isEditMode
            ? "Edit Template"
            : isFromSample
              ? "Customize Sample Template"
              : "Create Template"}
        </h3>
        {isFromSample && (
          <p className="text-muted small">
            Based on sample: <strong>{initialTemplate.name}</strong>. You can edit the layout and optionally replace the background image.
          </p>
        )}
        {isEditMode && (
          <p className="text-muted small">
            Update the template layout, name, or category. Background image is optional unless you choose a new file.
          </p>
        )}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Template Name</Form.Label>
            <Form.Control
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter template name"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Template Category</Form.Label>
            <Form.Select
              value={templateCategory}
              onChange={(e) => setTemplateCategory(e.target.value)}
            >
              <option value="">Select Category</option>
              <option value="Marketing">Marketing</option>
              <option value="Education">Education</option>
              <option value="Health">Health</option>
              <option value="Property">Property</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Upload Background Image
              {isFromSample || isEditMode ? " (optional)" : ""}
              <span className="fw-normal text-muted ms-1">· max 5 MB</span>
            </Form.Label>
            <div className="d-flex align-items-center">
              <Form.Label
                htmlFor="fileUpload"
                className="btn btn-outline-primary me-2 mb-0"
              >
                Choose File
              </Form.Label>
              <span>
                {fileName ||
                  ((isFromSample || isEditMode) && loadTemplate?.templateImage
                    ? "Using existing image"
                    : "No file chosen")}
              </span>
              <Form.Control
                ref={fileInputRef}
                id="fileUpload"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />
            </div>
            {imageError && (
              <Form.Text className="text-danger d-block mt-1">{imageError}</Form.Text>
            )}
          </Form.Group>

          <PrimaryButton onClick={handleSave}>
            {isEditMode ? "Update" : "Save"}
          </PrimaryButton>
          <SecondaryButton className="ms-2" onClick={onBack}>
            Cancel
          </SecondaryButton>
        </Form>
      </Col>
    </Row>
  );
};

export default TemplateCreation;